import { Member, Contribution, Event, Expense, Town } from '../types';
import { sanitizeInput } from './sanitize';
import { pdfExportService, PDFReportTemplate, ChartDataPoint } from '../services/PDFExportService.backend';
import { pdfErrorHandler, PDFGenerationResult, validateAndSanitizeData, withPDFErrorHandling } from './pdfErrorHandler';

// Enhanced export functions with PDF support
export const exportToPDF = async (
  data: any[],
  filename: string,
  template?: PDFReportTemplate
): Promise<void> => {
  try {
    // Validate input data
    const validation = validateAndSanitizeData(data, 'financial' as any);
    if (!validation.success) {
      const errorMessage = pdfErrorHandler.getUserFriendlyMessage(validation.error!);
      console.error('PDF export validation failed:', validation.error);
      throw new Error(errorMessage);
    }

    // Create a simple PDF using jsPDF for basic data export
    const doc = await createBasicPDF(validation.data || data, filename, template);

    // Trigger download
    const blob = new Blob([doc.output('blob')], { type: 'application/pdf' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `${filename}.pdf`;
    link.style.visibility = 'hidden';

    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);

    console.log(`Successfully exported ${data.length} records to ${filename}.pdf`);
  } catch (error) {
    console.error('PDF export error:', error);
    throw error; // Re-throw for caller to handle
  }
};

// Enhanced members report with PDF export
export const exportMembersReportPDF = async (
  members: Member[],
  towns: Town[],
  selectedTownId?: string,
  includeCharts: boolean = false
): Promise<void> => {
  try {
    console.log('Preparing members report PDF...');

    const filteredMembers = selectedTownId && selectedTownId !== 'all'
      ? members.filter(m => m.townId === selectedTownId)
      : members;

    // Validate members data
    const validation = validateAndSanitizeData(filteredMembers, 'members');
    if (!validation.success) {
      const errorMessage = pdfErrorHandler.getUserFriendlyMessage(validation.error!);
      console.error('Members report validation failed:', validation.error);
      throw new Error(errorMessage);
    }

    if (filteredMembers.length === 0) {
      const townName = selectedTownId === 'all' ? 'all towns' : towns.find(t => t.id === selectedTownId)?.name || 'selected town';
      console.warn(`No members found for ${townName}`);
      return;
    }

    const townName = selectedTownId === 'all'
      ? 'All_Towns'
      : towns.find(t => t.id === selectedTownId)?.name || 'Unknown_Town';

    const filename = `Members_Report_${townName}_${new Date().toISOString().slice(0, 10)}`;

    // Use PDF service for professional report
    const pdfBlob = await pdfExportService.generateMembersReport(
      filteredMembers,
      towns,
      selectedTownId
    );

    // Trigger download
    const url = URL.createObjectURL(pdfBlob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `${filename}.pdf`;
    link.style.visibility = 'hidden';

    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);

    console.log(`Successfully exported members report to ${filename}.pdf`);
  } catch (error) {
    console.error('Members report PDF error:', error);
    throw error; // Re-throw for caller to handle
  }
};

// Enhanced financial report with PDF export
export const exportFinancialReportPDF = async (
  contributions: Contribution[],
  expenses: Expense[],
  towns: Town[],
  members: Member[],
  selectedTownId?: string,
  includeCharts: boolean = false
): Promise<void> => {
  try {
    console.log('Preparing financial report PDF...');

    const filteredContributions = selectedTownId && selectedTownId !== 'all'
      ? contributions.filter(c => c.townId === selectedTownId)
      : contributions;

    const filteredExpenses = selectedTownId && selectedTownId !== 'all'
      ? expenses.filter(e => e.townId === selectedTownId)
      : expenses;

    if (filteredContributions.length === 0 && filteredExpenses.length === 0) {
      const townName = selectedTownId === 'all' ? 'all towns' : towns.find(t => t.id === selectedTownId)?.name || 'selected town';
      console.warn(`No financial data found for ${townName}`);
      return;
    }

    const townName = selectedTownId === 'all'
      ? 'All_Towns'
      : towns.find(t => t.id === selectedTownId)?.name || 'Unknown_Town';

    const filename = `Financial_Report_${townName}_${new Date().toISOString().slice(0, 10)}`;

    // Use PDF service for professional report
    const pdfBlob = await pdfExportService.generateFinancialReport(
      filteredContributions,
      filteredExpenses,
      towns,
      members,
      selectedTownId
    );

    // Trigger download
    const url = URL.createObjectURL(pdfBlob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `${filename}.pdf`;
    link.style.visibility = 'hidden';

    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);

    console.log(`Successfully exported financial report to ${filename}.pdf`);
  } catch (error) {
    console.error('Financial report PDF error:', error);
    throw new Error('Failed to generate financial report PDF. Please try again.');
  }
};

// Enhanced expense report with PDF export
export const exportExpenseReportPDF = async (
  expenses: Expense[],
  towns: Town[],
  selectedTownId?: string,
  dateRange?: { startDate: string; endDate: string }
): Promise<void> => {
  try {
    console.log('Preparing expense report PDF...');

    let filteredExpenses = selectedTownId && selectedTownId !== 'all'
      ? expenses.filter(e => e.townId === selectedTownId)
      : expenses;

    if (dateRange) {
      filteredExpenses = filteredExpenses.filter(expense => {
        const expenseDate = new Date(expense.date);
        return expenseDate >= new Date(dateRange.startDate) && expenseDate <= new Date(dateRange.endDate);
      });
    }

    if (filteredExpenses.length === 0) {
      const townName = selectedTownId === 'all' ? 'all towns' : towns.find(t => t.id === selectedTownId)?.name || 'selected town';
      const dateInfo = dateRange ? ` between ${dateRange.startDate} and ${dateRange.endDate}` : '';
      console.warn(`No expenses found for ${townName}${dateInfo}`);
      return;
    }

    const townName = selectedTownId && selectedTownId !== 'all'
      ? towns.find(t => t.id === selectedTownId)?.name
      : 'All_Towns';

    const dateStr = dateRange
      ? `_${dateRange.startDate}_to_${dateRange.endDate}`
      : '';

    const filename = `Expense_Report_${townName}${dateStr}_${new Date().toISOString().slice(0, 10)}`;

    // Create expense report data
    const reportData = filteredExpenses.map(expense => {
      const town = towns.find(t => t.id === expense.townId);
      return {
        Date: expense.date,
        Town: town?.name || 'Unknown',
        Category: expense.category,
        Amount: expense.amount,
        Description: expense.description,
        Beneficiary: expense.beneficiary || '',
        Reference: expense.reference || '',
        'Approval Status': expense.approvalStatus || 'approved',
        'Approved By': expense.approvedBy || '',
        'Approved Date': expense.approvedDate || ''
      };
    });

    // Use basic PDF export for expense report
    await exportToPDF(reportData, filename);
  } catch (error) {
    console.error('Expense report PDF error:', error);
    throw new Error('Failed to generate expense report PDF. Please try again.');
  }
};

// Enhanced event report with PDF export
export const exportEventReportPDF = async (
  events: Event[],
  towns: Town[],
  selectedTownId?: string,
  dateRange?: { startDate: string; endDate: string }
): Promise<void> => {
  try {
    console.log('Preparing event report PDF...');

    let filteredEvents = selectedTownId && selectedTownId !== 'all'
      ? events.filter(e => e.townId === selectedTownId)
      : events;

    // Only apply date filtering if events have valid dates
    if (dateRange) {
      filteredEvents = filteredEvents.filter(event => {
        if (!event.date || event.date === 'invalid-date') return true;
        const eventDate = new Date(event.date);
        if (isNaN(eventDate.getTime())) return true;
        const startDate = new Date(dateRange.startDate);
        const endDate = new Date(dateRange.endDate);
        return eventDate >= startDate && eventDate <= endDate;
      });
    }

    if (filteredEvents.length === 0) {
      const townName = selectedTownId === 'all' ? 'all towns' : towns.find(t => t.id === selectedTownId)?.name || 'selected town';
      const dateInfo = dateRange ? ` between ${dateRange.startDate} and ${dateRange.endDate}` : '';
      console.warn(`No events found for ${townName}${dateInfo}`);
      return;
    }

    const townName = selectedTownId && selectedTownId !== 'all'
      ? towns.find(t => t.id === selectedTownId)?.name
      : 'All_Towns';

    const dateStr = dateRange
      ? `_${dateRange.startDate}_to_${dateRange.endDate}`
      : '';

    const filename = `Event_Report_${townName}${dateStr}_${new Date().toISOString().slice(0, 10)}`;

    // Create event report data
    const reportData = filteredEvents.map(event => {
      const town = towns.find(t => t.id === event.townId);
      return {
        Date: event.date === 'invalid-date' ? 'Not Set' : event.date,
        Time: event.time,
        Town: town?.name || 'Unknown',
        Title: event.title,
        Type: event.type,
        Location: event.location,
        Description: event.description || '',
        'Max Attendees': event.maxAttendees || 'Unlimited',
        'Registered': event.attendees?.length || 0,
        'Attendance Rate': event.maxAttendees
          ? `${((event.attendees?.length || 0) / event.maxAttendees * 100).toFixed(1)}%`
          : 'N/A'
      };
    });

    // Use basic PDF export for event report
    await exportToPDF(reportData, filename);
  } catch (error) {
    console.error('Event report PDF error:', error);
    throw new Error('Failed to generate event report PDF. Please try again.');
  }
};

// Enhanced benefits report with PDF export
export const exportBenefitsReportPDF = async (
  benefits: any[],
  members: Member[],
  towns: Town[],
  selectedTownId?: string,
  dateRange?: { startDate: string; endDate: string }
): Promise<void> => {
  try {
    let filteredBenefits = selectedTownId && selectedTownId !== 'all'
      ? benefits.filter(b => b.townId === selectedTownId)
      : benefits;

    if (dateRange) {
      filteredBenefits = filteredBenefits.filter(benefit => {
        const requestDate = new Date(benefit.requestDate);
        return requestDate >= new Date(dateRange.startDate) && requestDate <= new Date(dateRange.endDate);
      });
    }

    if (filteredBenefits.length === 0) {
      const townName = selectedTownId === 'all' ? 'all towns' : towns.find(t => t.id === selectedTownId)?.name || 'selected town';
      const dateInfo = dateRange ? ` between ${dateRange.startDate} and ${dateRange.endDate}` : '';
      console.warn(`No benefits found for ${townName}${dateInfo}`);
      return;
    }

    const townName = selectedTownId && selectedTownId !== 'all'
      ? towns.find(t => t.id === selectedTownId)?.name
      : 'All_Towns';

    const dateStr = dateRange
      ? `_${dateRange.startDate}_to_${dateRange.endDate}`
      : '';

    const filename = `Benefits_Report_${townName}${dateStr}_${new Date().toISOString().slice(0, 10)}`;

    // Create benefits report data
    const reportData = filteredBenefits.map(benefit => {
      const member = members.find(m => m.id === benefit.memberId);
      const town = towns.find(t => t.id === benefit.townId);
      return {
        'Request Date': benefit.requestDate,
        'Member Name': member?.name || 'Unknown',
        'Member ID': member?.username || 'Unknown',
        Town: town?.name || 'Unknown',
        Type: benefit.type,
        Amount: benefit.amount,
        Description: benefit.description,
        Status: benefit.status,
        'Approved By': benefit.approvedBy || '',
        'Approved Date': benefit.approvedDate || '',
        'Paid Date': benefit.paidDate || '',
        Reference: benefit.reference || ''
      };
    });

    // Use basic PDF export for benefits report
    await exportToPDF(reportData, filename);
  } catch (error) {
    console.error('Benefits report PDF error:', error);
    throw new Error('Failed to generate benefits report PDF. Please try again.');
  }
};

// Enhanced audit report with PDF export
export const exportAuditReportPDF = async (
  auditLogs: any[],
  dateRange?: { startDate: string; endDate: string }
): Promise<void> => {
  try {
    let filteredLogs = auditLogs;

    if (dateRange) {
      filteredLogs = auditLogs.filter(log => {
        const logDate = new Date(log.timestamp);
        return logDate >= new Date(dateRange.startDate) && logDate <= new Date(dateRange.endDate);
      });
    }

    if (filteredLogs.length === 0) {
      const dateInfo = dateRange ? ` between ${dateRange.startDate} and ${dateRange.endDate}` : '';
      console.warn(`No audit logs found${dateInfo}`);
      return;
    }

    const dateStr = dateRange
      ? `_${dateRange.startDate}_to_${dateRange.endDate}`
      : '';

    const filename = `Audit_Report${dateStr}_${new Date().toISOString().slice(0, 10)}`;

    // Create audit report data
    const reportData = filteredLogs.map(log => ({
      Timestamp: sanitizeInput(log.timestamp),
      User: sanitizeInput(log.userName),
      Action: sanitizeInput(log.action),
      'Entity Type': sanitizeInput(log.entityType),
      'Entity ID': sanitizeInput(log.entityId),
      'Old Data': sanitizeInput(log.oldData || ''),
      'New Data': sanitizeInput(log.newData || '')
    }));

    // Use basic PDF export for audit report
    await exportToPDF(reportData, filename);
  } catch (error) {
    console.error('Audit report PDF error:', error);
    throw new Error('Failed to generate audit report PDF. Please try again.');
  }
};

// Create basic PDF using jsPDF
async function createBasicPDF(data: any[], filename: string, template?: PDFReportTemplate): Promise<any> {
  const jsPDF = (await import('jspdf')).default;
  const doc = new jsPDF({
    orientation: template?.layout?.pageOrientation || 'portrait',
    unit: 'mm',
    format: 'a4'
  });

  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();

  // Add header
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(16);
  doc.setTextColor(37, 99, 235); // Primary blue color
  doc.text(filename, 20, 20);

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(12);
  doc.setTextColor(31, 41, 55); // Gray 800
  doc.text(`Generated: ${new Date().toLocaleDateString()}`, 20, 30);
  doc.text(`Records: ${data.length}`, 20, 40);

  // Add table headers
  const headers = Object.keys(data[0] || {});
  const rows = data.map(item => headers.map(header => item[header]));

  doc.setFont('helvetica', 'bold');
  doc.setTextColor(37, 99, 235);
  let xPosition = 20;
  headers.forEach((header, index) => {
    doc.text(header, xPosition, 60);
    xPosition += 30;
  });

  // Add data rows
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(31, 41, 55);
  let yPosition = 70;
  let currentPage = 1;

  rows.forEach((row, rowIndex) => {
    // Check if we need a new page
    if (yPosition > pageHeight - 30) {
      doc.addPage();
      currentPage++;
      yPosition = 20;

      // Add header to new page
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(16);
      doc.setTextColor(37, 99, 235);
      doc.text(`${filename} - Page ${currentPage}`, 20, 20);
      yPosition = 40;
    }

    xPosition = 20;
    row.forEach((cell, cellIndex) => {
      const cellText = String(cell);
      const cellWidth = 25;

      // Handle long text by wrapping
      const lines = doc.splitTextToSize(cellText, cellWidth);
      doc.text(lines, xPosition, yPosition);
      xPosition += 30;
    });

    yPosition += 10;
  });

  // Add footer
  doc.setFont('helvetica', 'italic');
  doc.setFontSize(10);
  doc.setTextColor(156, 163, 175); // Gray 500
  doc.text(`Generated by Ghana Town Membership System • Page ${currentPage}`, 20, pageHeight - 10);

  return doc;
}

// Generate single payment receipt
export const generatePaymentReceiptPDF = async (
  contribution: Contribution,
  member: Member,
  town: Town
): Promise<void> => {
  const jsPDF = (await import('jspdf')).default;
  const doc = new jsPDF({
    orientation: 'portrait',
    unit: 'mm',
    format: 'a5' // Invoice/Receipt size
  });

  // Header
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(18);
  doc.setTextColor(37, 99, 235);
  doc.text(town.name, 105, 20, { align: 'center' });

  doc.setFontSize(14);
  doc.setTextColor(31, 41, 55);
  doc.text('Payment Receipt', 105, 30, { align: 'center' });

  // Receipt Details Box
  doc.setDrawColor(200, 200, 200);
  doc.setFillColor(249, 250, 251);
  doc.rect(15, 40, 118, 90, 'FD');

  doc.setFontSize(10);
  doc.setTextColor(100, 100, 100);

  // Left Column
  doc.text('Receipt No:', 20, 50);
  doc.text('Date:', 20, 60);
  doc.text('Member:', 20, 70);
  doc.text('Payment Method:', 20, 80);

  // Right Column Values
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(0, 0, 0);
  doc.text(contribution.reference || 'N/A', 80, 50);
  doc.text(contribution.date, 80, 60);
  doc.text(member.name, 80, 70);
  doc.text(contribution.paymentMethod, 80, 80);

  // Amount Section
  doc.setDrawColor(37, 99, 235);
  doc.setLineWidth(0.5);
  doc.line(20, 90, 128, 90);

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(12);
  doc.text('Amount Paid', 20, 100);
  doc.setFontSize(16);
  doc.setTextColor(22, 163, 74); // Green
  doc.text(`${contribution.currency || 'GHS'} ${contribution.amount.toFixed(2)}`, 128, 100, { align: 'right' });

  doc.setFontSize(10);
  doc.setTextColor(0, 0, 0);
  doc.text(contribution.type, 20, 110);

  doc.setFont('helvetica', 'italic');
  doc.setFontSize(8);
  doc.setTextColor(150, 150, 150);
  doc.text(`Status: ${contribution.status}`, 128, 110, { align: 'right' });

  // Footer
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(8);
  doc.setTextColor(150, 150, 150);
  doc.text('Thank you for your contribution.', 105, 140, { align: 'center' });
  doc.text(`Generated on ${new Date().toLocaleString()}`, 105, 145, { align: 'center' });

  doc.save(`Receipt_${contribution.reference || 'Draft'}.pdf`);
};

// Export all enhanced export functions
export const enhancedExports = {
  exportToPDF,
  generatePaymentReceiptPDF,
  exportMembersReportPDF,
  exportFinancialReportPDF,
  exportExpenseReportPDF,
  exportEventReportPDF,
  exportBenefitsReportPDF,
  exportAuditReportPDF
};